import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { createAppTheme, applyThemeVars } from "../../theme";

const STORAGE_KEY = "smartdx.theme";
// Lo que el médico ELIGE (preferencia) no es lo que se pinta (mode): con
// "system" el modo sale del sistema operativo y cambia solo si el SO cambia.
// En storage se guarda la preferencia, así "system" sobrevive a la recarga.
const VALID_PREFERENCES = new Set(["light", "dark", "system"]);
const SYSTEM_QUERY = "(prefers-color-scheme: light)";

const readInitialPreference = () => {
  if (typeof window === "undefined") return "dark";
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && VALID_PREFERENCES.has(stored)) return stored;
  } catch (_) {
    /* localStorage puede fallar en Safari private mode */
  }
  return "dark";
};

const readSystemMode = () => {
  if (typeof window === "undefined" || !window.matchMedia) return "dark";
  return window.matchMedia(SYSTEM_QUERY).matches ? "light" : "dark";
};

const ThemeModeContext = createContext({
  mode: "dark",
  preference: "dark",
  setMode: () => {},
  toggle: () => {},
});

export const useThemeMode = () => useContext(ThemeModeContext);

export const AppThemeProvider = ({ children }) => {
  const [preference, setPreference] = useState(readInitialPreference);
  const [systemMode, setSystemMode] = useState(readSystemMode);
  const mode = preference === "system" ? systemMode : preference;

  // Aplica CSS vars antes del primer paint para evitar flash de tema incorrecto.
  // Como useState ya tiene el valor sincrónico de localStorage, esto corre
  // antes que el primer render efectivo.
  if (typeof document !== "undefined" && !document.documentElement.dataset.themeApplied) {
    applyThemeVars(mode);
    document.documentElement.dataset.themeApplied = "1";
  }

  // Solo se escucha al SO mientras la preferencia es "system".
  useEffect(() => {
    if (preference !== "system" || !window.matchMedia) return undefined;
    const mql = window.matchMedia(SYSTEM_QUERY);
    const onChange = () => setSystemMode(mql.matches ? "light" : "dark");
    onChange();
    mql.addEventListener?.("change", onChange);
    return () => mql.removeEventListener?.("change", onChange);
  }, [preference]);

  useEffect(() => {
    applyThemeVars(mode);
  }, [mode]);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, preference);
    } catch (_) {
      /* noop */
    }
  }, [preference]);

  const value = useMemo(
    () => ({
      mode,
      preference,
      setMode: (next) => {
        if (VALID_PREFERENCES.has(next)) setPreference(next);
      },
      toggle: () => setPreference(mode === "dark" ? "light" : "dark"),
    }),
    [mode, preference],
  );

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

export default AppThemeProvider;
